import { Box, Typography } from "@mui/material";
import { useEffect } from "react";
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  UseFormStateReturn,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { useAppSelector } from "../../../../../../../app/hooks";
import isEmpty from "../../../../../../../common/helpers/isEmpty";
import {
  FormKeys,
  isOtherPeriodWithFullOrPartTimeWorkTime,
  isOtherPeriodWithVariableWorkTime,
  isWeeklyPeriodWithFullOrPartTimeWorkTime,
  isWeeklyPeriodWithVariableWorkTime,
} from "../../helpers";
import { CustomInput } from "./CustomInput";

interface NumberFieldProps {
  field: ControllerRenderProps<FieldValues, any>;
  fieldState: ControllerFieldState;
  formState: UseFormStateReturn<FieldValues>;
  isDisabled: boolean;
  decimalScale?: number;
}

const NumberField = ({
  field: { name, value, onChange },
  fieldState,
  isDisabled,
  decimalScale = 2,
}: NumberFieldProps) => (
  <NumericFormat
    name={name}
    value={value}
    onValueChange={(values) => onChange(values.value)}
    valueIsNumericString
    decimalSeparator=","
    allowNegative={false}
    decimalScale={decimalScale}
    customInput={CustomInput}
    error={!!fieldState.error}
    disabled={isDisabled}
  />
);

enum RowType {
  WeeklyPeriodWithVariableWorkTime = "ROW_WEEKLY_VARIABLE",
  WeeklyPeriodWithFullOrPartTimeWorkTime = "ROW_WEEKLY_FULL_OR_PART_TIME",
  OtherPeriodWithVariableWorkTime = "ROW_OTHER_VARIABLE",
  OtherPeriodWithFullOrPartTimeWorkTime = "ROW_OTHER_FULL_OR_PART_TIME",
}

type Props = {
  isReadOnly?: boolean;
};

export const PeriodWorkHours = ({ isReadOnly = false }: Props) => {
  const { t } = useTranslation();
  const { control, watch, resetField, setValue } = useFormContext();
  const watchedWorkPeriodType = watch(FormKeys.PeriodType);
  const watchedWorkTimeType = watch(FormKeys.WorkTimeType);
  const watchedWorkPeriodRowType = watch(FormKeys.WorkPeriodRowType);

  const { status } = useAppSelector((state) => state.supplements);
  const isError = status === "failed";

  /** Specification from design:
   *
   * "VIIKKOTYÖAIKA / JAKSOAIKA FIELDS WILL BE SHOWN BASED ON THE TES VARIATIONS"
   *
   * Whether it is per week or per period comes from the variation.
   *
   * And whether Viikkotyöaika or Jaksotyöaika has one or two time boxes where you can enter a number
   * depends if you have chose either Kokokainen or Osa-aikanen you only have on box.
   *
   * If you choose Vaihteleva työaika when you have the two boxes.
   */

  // Check if shown row needs to be changed based on workPeriodType and workTimeType selections
  // Reset all fields if shown row is changed (resets values and validation errors)
  useEffect(() => {
    const resetFields = () => {
      resetField(FormKeys.MinHours);
      resetField(FormKeys.MaxHours);
      resetField(FormKeys.WeeksInPeriod);
    };

    const changeRowTypeTo = (newRowType: RowType) => {
      if (watchedWorkPeriodRowType !== newRowType) {
        // Row type changes
        resetFields();
        setValue(FormKeys.WorkPeriodRowType, newRowType);
      }
    };

    if (
      isWeeklyPeriodWithVariableWorkTime(
        watchedWorkPeriodType,
        watchedWorkTimeType,
      )
    ) {
      changeRowTypeTo(RowType.WeeklyPeriodWithVariableWorkTime);
    }

    if (
      isWeeklyPeriodWithFullOrPartTimeWorkTime(
        watchedWorkPeriodType,
        watchedWorkTimeType,
      )
    ) {
      changeRowTypeTo(RowType.WeeklyPeriodWithFullOrPartTimeWorkTime);
    }

    if (
      isOtherPeriodWithVariableWorkTime(
        watchedWorkPeriodType,
        watchedWorkTimeType,
      )
    ) {
      changeRowTypeTo(RowType.OtherPeriodWithVariableWorkTime);
    }

    if (
      isOtherPeriodWithFullOrPartTimeWorkTime(
        watchedWorkPeriodType,
        watchedWorkTimeType,
      )
    ) {
      changeRowTypeTo(RowType.OtherPeriodWithFullOrPartTimeWorkTime);
    }
  }, [
    resetField,
    setValue,
    watchedWorkPeriodRowType,
    watchedWorkPeriodType,
    watchedWorkTimeType,
  ]);

  const bothValuesExist = (firstValue: string, secondValue: string) => {
    return !isEmpty(firstValue) && !isEmpty(secondValue);
  };

  const isValidRange = (smallerValue: string, largerValue: string) => {
    return +smallerValue <= +largerValue;
  };

  /**
   * FIXME error in range validation handling:
   * Add small value 60
   * Add large value 30 -> error
   * Change small value to 20
   * -> Valid range (20 - 30), but we still have error in large value field
   *
   * -> Need to do validation in useEffect and check both values?
   *
   */
  const validateRangeIfValuesExist = (
    smallerValue: string,
    largerValue: string,
  ) => {
    if (bothValuesExist(smallerValue, largerValue)) {
      if (isValidRange(smallerValue, largerValue)) {
        return true;
      }
      return false;
    }
    return true;
  };

  if (watchedWorkPeriodRowType === RowType.WeeklyPeriodWithVariableWorkTime) {
    return (
      <>
        <Controller
          control={control}
          name={FormKeys.MinHours}
          key={FormKeys.MinHours}
          rules={{
            required: {
              value: true,
              message: t("common:requiredField"),
            },
            validate: {
              validValue: (value) =>
                validateRangeIfValuesExist(value, watch(FormKeys.MaxHours)),
            },
          }}
          render={(props) => <NumberField {...props} isDisabled={isReadOnly} />}
        />
        <Box padding="0 1rem 0 1rem" alignSelf="center">
          -
        </Box>
        <Controller
          control={control}
          name={FormKeys.MaxHours}
          key={FormKeys.MaxHours}
          rules={{
            required: {
              value: true,
              message: t("common:requiredField"),
            },
            validate: {
              validValue: (value) =>
                validateRangeIfValuesExist(watch(FormKeys.MinHours), value),
            },
          }}
          render={(props) => <NumberField {...props} isDisabled={isReadOnly} />}
        />
        <Typography alignSelf="center" paddingLeft="0.5rem">
          {t("order:hoursPerWeek")}
        </Typography>
      </>
    );
  }

  if (
    watchedWorkPeriodRowType === RowType.WeeklyPeriodWithFullOrPartTimeWorkTime
  ) {
    return (
      <>
        <Controller
          control={control}
          name={FormKeys.MinHours}
          key={FormKeys.MinHours}
          rules={{ required: true }}
          render={(props) => <NumberField {...props} isDisabled={isReadOnly} />}
        />
        <Typography alignSelf="center" padding="0 0.5rem 0 0.5rem">
          {t("order:hoursPerWeek")}
        </Typography>
      </>
    );
  }

  if (watchedWorkPeriodRowType === RowType.OtherPeriodWithVariableWorkTime) {
    return (
      <>
        <Controller
          control={control}
          name={FormKeys.MinHours}
          key={FormKeys.MinHours}
          rules={{
            required: {
              value: true,
              message: t("common:requiredField"),
            },
            validate: {
              validValue: (value) =>
                validateRangeIfValuesExist(value, watch(FormKeys.MaxHours)),
            },
          }}
          render={(props) => <NumberField {...props} isDisabled={isReadOnly} />}
        />
        <Box padding="0 1rem 0 1rem" alignSelf="center">
          -
        </Box>
        <Controller
          control={control}
          name={FormKeys.MaxHours}
          key={FormKeys.MaxHours}
          rules={{
            required: {
              value: true,
              message: t("common:requiredField"),
            },
            validate: {
              validValue: (value) =>
                validateRangeIfValuesExist(watch(FormKeys.MinHours), value),
            },
          }}
          render={(props) => <NumberField {...props} isDisabled={isReadOnly} />}
        />
        <Typography alignSelf="center" padding="0 0.5rem 0 0.5rem">
          {t("order:hoursPer")}
        </Typography>
        <Controller
          control={control}
          name={FormKeys.WeeksInPeriod}
          key={FormKeys.WeeksInPeriod}
          rules={{ required: true }}
          render={(props) => (
            <NumberField {...props} isDisabled={isReadOnly} decimalScale={0} />
          )}
        />
        <Typography alignSelf="center" paddingLeft="0.5rem">
          {t("order:weeks")}
        </Typography>
      </>
    );
  }

  if (
    watchedWorkPeriodRowType === RowType.OtherPeriodWithFullOrPartTimeWorkTime
  ) {
    return (
      <>
        <Controller
          control={control}
          name={FormKeys.MinHours}
          key={FormKeys.MinHours}
          rules={{ required: true }}
          render={(props) => <NumberField {...props} isDisabled={isReadOnly} />}
        />
        <Typography alignSelf="center" padding="0 0.5rem 0 0.5rem">
          {t("order:hoursPer")}
        </Typography>
        <Controller
          control={control}
          name={FormKeys.WeeksInPeriod}
          key={FormKeys.WeeksInPeriod}
          rules={{ required: true }}
          render={(props) => (
            <NumberField {...props} isDisabled={isReadOnly} decimalScale={0} />
          )}
        />
        <Typography alignSelf="center" paddingLeft="0.5rem">
          {t("order:weeks")}
        </Typography>
      </>
    );
  }

  if (isError) {
    return (
      <Typography color="error">{t("errors:somethingWentWrong")}</Typography>
    );
  }

  return <></>;
};
